import Swal from "sweetalert2";

const GUEST_CART_KEY = "guest_cart_items";
const GUEST_CART_LIMIT_MESSAGE = `You already have an artwork in your cart.
To support one artist at a time, please complete or remove the current item before adding another.`;

const normalizeId = (value) => String(value ?? "").trim();

const readGuestCart = () => {
  try {
    const raw = localStorage.getItem(GUEST_CART_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed : [];
  } catch (error) {
    return [];
  }
};

const writeGuestCart = (items) => {
  localStorage.setItem(GUEST_CART_KEY, JSON.stringify(items || []));
};

const buildCartItem = (product, quantity = 1) => {
  const productId = normalizeId(
    product?.product_id ||
    product?.productId ||
    product?.id ||
    product?._id
  );
  if (!productId) return null;
  return {
    product_id: productId,
    variant_id: product?.variant_id || null,
    design_name: product?.design_name || product?.variant_name || "",
    variant_name: product?.variant_name || product?.design_name || "",
    design_image: product?.design_image || "",
    name: product?.name || product?.title || "Artwork",
    image: product?.design_image || product?.image || product?.images?.[0] || "",
    price: Number(product?.price || 0),
    quantity: Math.max(1, Number(quantity || 1))
  };
};

const showOneArtworkAtATimeAlert = (onViewCart) => {
  return Swal.fire({
    icon: "warning",
    title: "One Artwork at a Time 🎨",
    text: GUEST_CART_LIMIT_MESSAGE,
    showCancelButton: true,
    reverseButtons: true,
    confirmButtonText: "View Cart",
    cancelButtonText: "Continue Browsing",
    confirmButtonColor: "#f97316",
    cancelButtonColor: "#64748b",
    background: "#ffffff",
    buttonsStyling: true,
    customClass: {
      popup: "swal-custom-popup"
    }
  }).then((result) => {
    if (result.isConfirmed) {
      if (typeof onViewCart === "function") {
        onViewCart();
      } else if (typeof window !== "undefined") {
        window.location.assign("/shop-cart");
      }
    }
    return result;
  });
};

const addGuestCartItem = (product, quantity = 1) => {
  const cart = readGuestCart();
  const newItem = buildCartItem(product, quantity);
  if (!newItem) {
    return { accepted: false, blocked: false, cart };
  }

  const existing = cart.find(
    (item) => normalizeId(item?.product_id) === newItem.product_id
  );

  if (existing) {
    existing.quantity = Math.max(
      1,
      Number(existing.quantity || 0) + Number(newItem.quantity || 1)
    );
    writeGuestCart(cart);
    return { accepted: true, blocked: false, cart };
  }

  const anyCartItem = cart[0];
  if (anyCartItem && normalizeId(anyCartItem?.product_id) !== newItem.product_id) {
    showOneArtworkAtATimeAlert(() => window.location.assign("/shop-cart"));
    return { accepted: false, blocked: true, cart };
  }

  cart.push(newItem);
  writeGuestCart(cart);
  return { accepted: true, blocked: false, cart };
};

const updateGuestCartItemQuantity = (productId, quantity) => {
  const cart = readGuestCart();
  const id = normalizeId(productId);
  const nextQty = Math.max(1, Number(quantity || 1));
  const updated = cart.map((item) =>
    normalizeId(item?.product_id) === id
      ? { ...item, quantity: nextQty }
      : item
  );
  writeGuestCart(updated);
  return updated;
};

const removeGuestCartItem = (productId) => {
  const id = normalizeId(productId);
  const cart = readGuestCart().filter(
    (item) => normalizeId(item?.product_id) !== id
  );
  writeGuestCart(cart);
  return cart;
};

const clearGuestCart = () => {
  localStorage.removeItem(GUEST_CART_KEY);
};

const getGuestCartItems = () => readGuestCart();

const getGuestCartIds = () =>
  readGuestCart()
    .map((item) => normalizeId(item?.product_id))
    .filter((id) => id);

const getGuestCartCount = () =>
  readGuestCart().reduce((sum, item) => sum + Number(item?.quantity || 0), 0);

export {
  GUEST_CART_KEY,
  GUEST_CART_LIMIT_MESSAGE,
  showOneArtworkAtATimeAlert,
  addGuestCartItem,
  updateGuestCartItemQuantity,
  removeGuestCartItem,
  clearGuestCart,
  getGuestCartItems,
  getGuestCartIds,
  getGuestCartCount
};
